<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('versioningConfigs.title') }}</h1>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('versioningConfigs.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('versioningConfigs.modelName') }}</th>
                  <th>{{ $t('versioningConfigs.description') }}</th>
                  <th>{{ $t('versioningConfigs.enabled') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="config in configs" :key="config.id">
                  <td><strong>{{ config.tracked_model_name }}</strong></td>
                  <td>{{ config.description || '-' }}</td>
                  <td>
                    <v-chip
                      :color="config.enabled ? 'success' : 'default'"
                      size="small"
                    >
                      {{ config.enabled ? $t('common.yes') : $t('common.no') }}
                    </v-chip>
                  </td>
                  <td>
                    <v-btn
                      variant="plain"
                      size="small"
                      :color="config.enabled ? 'warning' : 'success'"
                      @click="toggleConfig(config)"
                      :icon="config.enabled ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch'"
                    ></v-btn>
                    <v-btn
                      variant="plain"
                      size="small"
                      @click="editConfig(config)"
                      icon="mdi-pencil"
                      class="ml-1"
                    ></v-btn>
                  </td>
                </tr>
                <tr v-if="configs.length === 0">
                  <td colspan="4" class="text-center text-grey">{{ $t('versioningConfigs.noConfigsFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showEditDialog" max-width="600" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ $t('versioningConfigs.edit') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="configForm.tracked_model_name"
              :label="$t('versioningConfigs.modelName')"
              required
              density="compact"
              :disabled="!!configForm.id"
              :rules="[v => !!v || $t('versioningConfigs.modelNameRequired')]"
            />
            <v-textarea
              v-model="configForm.description"
              :label="$t('versioningConfigs.description')"
              rows="3"
              density="compact"
              class="mt-2"
            />
            <v-switch
              v-model="configForm.enabled"
              :label="$t('versioningConfigs.enabled')"
              density="compact"
              class="mt-2"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showEditDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveConfig">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPut, apiPost } from '../utils/api'

const { t } = useI18n()

interface VersioningConfig {
  id: number
  tracked_model_name: string
  enabled: boolean
  description: string | null
}

const configs = ref<VersioningConfig[]>([])
const showEditDialog = ref(false)
const configForm = ref({
  id: null as number | null,
  tracked_model_name: '',
  enabled: false,
  description: ''
})

const loadConfigs = async () => {
  try {
    const response = await apiGet('/api/versioning_configs')
    if (response.ok) {
      configs.value = await response.json()
    }
  } catch (error) {
    console.error('Load versioning configs error:', error)
  }
}

const toggleConfig = async (config: VersioningConfig) => {
  try {
    const response = await apiPut(`/api/versioning_configs/${config.id}`, {
      versioning_config: {
        enabled: !config.enabled
      }
    })
    if (response.ok) {
      await loadConfigs()
    } else {
      const data = await response.json()
      alert(data.errors?.join(', ') || t('versioningConfigs.failedToUpdate'))
    }
  } catch (error) {
    console.error('Toggle config error:', error)
    alert(t('versioningConfigs.failedToUpdate'))
  }
}

const editConfig = (config: VersioningConfig) => {
  configForm.value = {
    id: config.id,
    tracked_model_name: config.tracked_model_name,
    enabled: config.enabled,
    description: config.description || ''
  }
  showEditDialog.value = true
}

const saveConfig = async () => {
  try {
    if (configForm.value.id) {
      const response = await apiPut(`/api/versioning_configs/${configForm.value.id}`, {
        versioning_config: {
          description: configForm.value.description,
          enabled: configForm.value.enabled
        }
      })
      if (response.ok) {
        await loadConfigs()
        showEditDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('versioningConfigs.failedToSave'))
      }
    } else {
      const response = await apiPost('/api/versioning_configs', {
        versioning_config: {
          tracked_model_name: configForm.value.tracked_model_name,
          description: configForm.value.description,
          enabled: configForm.value.enabled
        }
      })
      if (response.ok) {
        await loadConfigs()
        showEditDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('versioningConfigs.failedToCreate'))
      }
    }
  } catch (error) {
    console.error('Save config error:', error)
    alert(t('versioningConfigs.failedToSave'))
  }
}

const resetForm = () => {
  configForm.value = {
    id: null,
    tracked_model_name: '',
    enabled: false,
    description: ''
  }
}

onMounted(() => {
  loadConfigs()
})
</script>
